import { useMemo } from 'react';
import type { ResponsiveSwatchPropValue, SwatchColorOption } from './types';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import type { SwatchColorGridProps } from './types';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';

const DEFAULT_COERCED_VALUES = [1, 1, 1] as number[];

export const coerceValuestoArray = (values?: ResponsiveSwatchPropValue<number>) => {
  if (!values) {
    return DEFAULT_COERCED_VALUES;
  }

  if (Array.isArray(values)) {
    const resultValue: number[] = [];

    DEFAULT_COERCED_VALUES.forEach((_, index) => {
      const value = values[index];
      if (value === null) {
        let arrayItem: number = values[0];
        if (index === 0) {
          arrayItem = 1;
        } else {
          for (let i = 0; i < index; i++) {
            const maybePrevNumber = values[index - i];

            if (typeof maybePrevNumber === 'number') {
              arrayItem = maybePrevNumber;
              break;
            }
          }
        }
        resultValue.push(arrayItem);
      } else {
        resultValue.push(value);
      }
    });
    return resultValue;
  }

  const resultValue = DEFAULT_COERCED_VALUES.map(x => Math.max(1, values));
  return resultValue;
};

export const useSwatchColorGrid = ({
  id,
  disableOptionOverflow,
  value,
  rows,
  columns,
  options,
  alignment,
  swatchSize,
  onSwatchClick,
  interactionType,
  onSwatchHover,
  onSwatchHoverExit,
  onInteract,
  checkOptionAvailability,
}: SwatchColorGridProps) => {
  alignment = alignment || 'left';
  swatchSize = swatchSize || 6;
  interactionType = interactionType || 'click';

  const coercedRow = coerceValuestoArray(rows);
  const coercedColumns = coerceValuestoArray(columns);
  const isMobileScreen = useIsMobileScreen();

  const [values] = useResponsive(
    {
      values: {
        mobile: {
          rows: coercedRow[0],
          columns: coercedColumns[0],
        },
        tablet: {
          rows: coercedRow[1],
          columns: coercedColumns[1],
        },
        desktop: {
          rows: coercedRow[2],
          columns: coercedColumns[2],
        },
      },
    },
    isMobileScreen ? { rows: coercedRow[0], columns: coercedColumns[0] } : { rows: coercedRow[2], columns: coercedColumns[2] },
  );
  const { rows: rowCount, columns: columnCount } = values;

  const { optionsToRender, overflowCount } = useMemo(() => {
    const optionsToRender = options.map(item => {
      const notAvailable = checkOptionAvailability ? !checkOptionAvailability(item.id, id) : false;
      return {
        ...item,
        notAvailable,
      };
    });
    if (disableOptionOverflow) {
      return {
        optionsToRender,
        overflowCount: 0,
      };
    }
    const optionCount = optionsToRender.length;
    const maxColorsToShow = rowCount * columnCount - (disableOptionOverflow ? 0 : 1);
    const overflowCount = optionCount <= maxColorsToShow ? 0 : optionCount - maxColorsToShow;
    const slicedOptionsToRender = optionsToRender.slice(0, maxColorsToShow);
    return { optionsToRender: slicedOptionsToRender, overflowCount };
  }, [disableOptionOverflow, rowCount, columnCount, options, id, checkOptionAvailability]);

  const handleMouseClick = (e: React.MouseEvent, option: SwatchColorOption) => {
    e.stopPropagation();
    if (interactionType === 'click' || interactionType === 'hover-n-click') {
      onInteract(e, option);
    }

    onSwatchClick && onSwatchClick(e, option);
  };

  const handleMouseOver = (e: React.MouseEvent, option: SwatchColorOption) => {
    if (interactionType === 'hover' || interactionType === 'hover-n-click') {
      onInteract(e, option);
    }

    onSwatchHover && onSwatchHover(e, option);
  };

  const handleMouseExit = (e: React.MouseEvent, option: SwatchColorOption) => {
    onSwatchHoverExit && onSwatchHoverExit(e, option);
  };
  const handleKeyPress = (e: React.KeyboardEvent, option: SwatchColorOption) => {
    if (e.key === 'Enter') {
      onInteract(e, option);
    }
  };

  return {
    optionsToRender,
    overflowCount,
    colorSelected: value,
    columnCount,
    alignment,
    swatchSize,
    handleMouseClick,
    handleMouseOver,
    handleMouseExit,
    handleKeyPress,
  };
};
